import * as qiniu from 'qiniu-js'
import CryptoJS from 'crypto-js'
import api from './api'
import { toast } from '../services/toastService'

const config = {
  region: 'z0', // z0 对应 华东-浙江 (up-z0.qiniup.com)
  imageURLPrefix: 'https://img.jixianxueyuan.com/',
  videoURLPrefix: 'https://video.jixianxueyuan.com/'
}

interface MediaInfo {
  path: string
  type?: string
  width?: number
  height?: number
  uniqueId?: string
  uploadType?: string
}

interface VideoInfo {
  path: string
  thumbnail?: string
  type?: string
  width?: number
  height?: number
}

interface UploadProgressCallback {
  (progress: number): void
}

interface UploadCompleteCallback {
  (media: MediaInfo): void
}

interface UploadErrorCallback {
  (error: any): void
}

interface UploadOptions {
  key: string
  token: string
  filePathOrDataURL: string
  prefixURL: string
  onProgress?: UploadProgressCallback
  isGenHash?: boolean // 是否生成哈希值，默认 true
}

class Uploader {
  /**
   * 上传视频
   */
  uploadVideo = async (
    filePath: string,
    seconds: number,
    onComplete: UploadCompleteCallback,
    onError?: UploadErrorCallback
  ): Promise<void> => {
    toast.loading('获取上传凭证...')
    try {
      const ret = await (api as any).request((api as any).uptoken_video, { seconds, hasCover: 0 })
      const token = (ret.data as any).uptoken
      const name = (ret.data as any).myParam
      const key = 'video_' + this.createFileName(filePath)

      toast.loading('资源上传中 (0%)...')

      const res = await this._upload({
        key,
        token,
        filePathOrDataURL: filePath,
        prefixURL: config.videoURLPrefix,
        onProgress: (progress) => {
          toast.loading(`资源上传中 (${progress}%)...`)
        }
      })

      const info: VideoInfo & MediaInfo = {
        path: res.url,
        thumbnail: config.videoURLPrefix + name,
        uniqueId: res.hash
      }
      
      toast.hideLoading()
      onComplete(info)
    } catch (error) {
      console.error('uploadVideo error:', error)
      toast.hideLoading()
      toast.error('视频上传失败')
      if (onError) {
        onError(error)
      }
    }
  }

  /**
   * 上传 DataURL (Base64)
   */
  uploadDataURL = async (
    dataURL: string,
    imgType: string,
    isGenHash?: boolean,
    onComplete?: (info: MediaInfo) => void,
    onError?: UploadErrorCallback
  ): Promise<MediaInfo> => {
    toast.loading('资源上传中...')
    // await new Promise(resolve => setTimeout(resolve, 2000))
    try {
      const ret = await (api as any).request((api as any).uptoken_picure)
      const token = (ret.data as any).uptoken
      const key = `${imgType}_` + this.createFileNameFromDataURL(dataURL)

      const res = await this._upload({
        key,
        token,
        filePathOrDataURL: dataURL,
        prefixURL: config.imageURLPrefix,
        isGenHash: isGenHash || false
      })

      const info: MediaInfo = {
        path: res.url,
        type: 'img',
        width: 0,
        height: 0,
        uniqueId: res.hash,
        uploadType: imgType
      }

      toast.hideLoading()
      if (onComplete) {
        onComplete(info)
      }
      return info
    } catch (error) {
      console.error('uploadDataURL error:', error)
      toast.hideLoading()
      toast.error('上传失败')
      if (onError) {
        onError(error)
      }
      throw error
    }
  }

  /**
   * 批量上传 DataURL
   */
  uploadBatch = async (
    items: { dataURL: string; type: string, isGenHash?: boolean }[]
  ): Promise<MediaInfo[]> => {
    toast.loading(`资源上传中 (0/${items.length})...`, {
      position: 'center',
      stackable: true
    })
    try {
      const ret = await (api as any).request((api as any).uptoken_picure)
      const token = (ret.data as any).uptoken

      let count = 0
      const results: MediaInfo[] = []

      const uploadTasks = items.map(async (item) => {
        const key = `${item.type}_` + this.createFileNameFromDataURL(item.dataURL)
        const res = await this._upload({
          key,
          token,
          filePathOrDataURL: item.dataURL,
          prefixURL: config.imageURLPrefix,
          isGenHash: item.isGenHash || false
        })
        
        count++
        toast.loading(`资源上传中 (${count}/${items.length})...`)
        
        const info: MediaInfo = {
          path: res.url,
          type: 'img',
          width: 0,
          height: 0,
          uniqueId: res.hash,
          uploadType: item.type
        }
        results.push(info)
        return info
      })

      const finalResults = await Promise.all(uploadTasks)
      toast.hideLoading()
      return finalResults
    } catch (error) {
      console.error('uploadBatch error:', error)
      toast.hideLoading()
      toast.error('批量上传失败')
      throw error
    }
  }

  /**
   * 核心上传方法 - 使用 qiniu-js SDK
   */
  private _upload = async (
    options: UploadOptions
  ): Promise<{ url: string; hash?: string; }> => {
    const { token, filePathOrDataURL, key, prefixURL, onProgress } = options
    if (!filePathOrDataURL) {
      throw new Error('filePath is required')
    }

    let file: File | Blob | null = null

    if (filePathOrDataURL.startsWith('data:')) {
      file = this.dataURLtoBlob(filePathOrDataURL)
    } else if (filePathOrDataURL.startsWith('http') || filePathOrDataURL.startsWith('/') || filePathOrDataURL.startsWith('blob:')) {
      file = await this.urlToFile(filePathOrDataURL, key)
    } else {
      throw new Error('Unsupported file path format')
    }

    if (!file) {
      throw new Error('Failed to create File/Blob from input')
    }

    const hash = options.isGenHash ? await this.calculateHash(file) : undefined
    const observable = qiniu.upload(file as any, key, token, {}, {
      // region: qiniu.region.z0 // 移除硬编码区域，qiniu-js 将根据 token 自动检测
    })

    return new Promise((resolve, reject) => {
      observable.subscribe({
        next: (res) => {
          if (onProgress) {
            onProgress(Math.round(res.total.percent))
          }
        },
        error: (err) => {
          console.error('Qiniu SDK upload error:', err)
          reject(err)
        },
        complete: (res) => {
          res.url = prefixURL + res.key
          if (options.isGenHash && hash && hash.length > 0) {
            res.hash = hash
          }
          resolve(res)
        }
      })
    })
  }

  private async calculateHash(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const buffer = e.target?.result
        if (buffer instanceof ArrayBuffer) {
          const wordArray = CryptoJS.lib.WordArray.create(buffer)
          const hash = CryptoJS.MD5(wordArray).toString()
          resolve(hash)
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'))
        }
      }
      reader.onerror = () => reject(new Error('FileReader error'))
      reader.readAsArrayBuffer(file)
    })
  }

  private createFileName(filePath: string): string {
    const parts = filePath.split('/')
    const last = parts[parts.length - 1] || ''
    const extParts = last.split('.')
    const ext = extParts.length > 1 ? extParts[extParts.length - 1] : 'png'

    const randomS4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
    const time = this.formatDate(new Date(), 'YYMMDDHHmmss')
    return `${time}${randomS4}.${ext}`
  }

  private createFileNameFromDataURL(dataURL: string): string {
    const randomS4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
    const time = this.formatDate(new Date(), 'YYMMDDHHmmss')
    const ext = dataURL.match(/data:image\/(\w+);/)?.[1] || 'png'
    return `${time}${randomS4}.${ext}`
  }

  private formatDate(date: Date, format: string): string {
    const year = date.getFullYear().toString()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')

    return format
      .replace('YYYY', year)
      .replace('YY', year.substring(2))
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds)
  }

  private dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  }

  private async urlToFile(url: string, fileName: string): Promise<File> {
    const response = await fetch(url)
    const blob = await response.blob()
    return new File([blob], fileName, { type: blob.type })
  }
}

export default new Uploader()
